import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

/* ============================================================
   THÈMES
   ============================================================ */
const THEMES = {
  dark: {
    page:         { bg: "#0a0a0a",                    color: "#fff" },
    card:         { bg: "rgba(255,255,255,0.03)",      border: "rgba(255,255,255,0.08)" },
    cardHeader:   { bg: "rgba(230,255,62,0.07)",       border: "rgba(230,255,62,0.15)",  color: "#e6ff3e" },
    cellLabel:    { bg: "rgba(255,255,255,0.05)",      color: "#64748b",  border: "rgba(255,255,255,0.06)" },
    cellInput:    { bg: "rgba(255,255,255,0.02)" },
    rowBorder:    "rgba(255,255,255,0.05)",
    input:        { bg: "rgba(255,255,255,0.05)",      border: "rgba(255,255,255,0.1)",  color: "#fff" },
    sectionTitle: { color: "#fff" },
    toggleBtn:    { bg: "rgba(255,255,255,0.06)",      border: "rgba(255,255,255,0.12)", color: "#fff" },
    accent:       "#e6ff3e",
    accentText:   "#000",
    saveBtn:      { bg: "rgba(255,255,255,0.08)",      border: "rgba(255,255,255,0.15)", color: "#fff" },
    modal:        { bg: "#111827",                     border: "rgba(255,255,255,0.1)" },
    modalDivider: "rgba(255,255,255,0.08)",
  },
  light: {
    page:         { bg: "#f1f5f9",    color: "#0f172a" },
    card:         { bg: "#fff",       border: "#e2e8f0" },
    cardHeader:   { bg: "#f8fafc",    border: "#e2e8f0", color: "#475569" },
    cellLabel:    { bg: "#f1f5f9",    color: "#475569",  border: "#e2e8f0" },
    cellInput:    { bg: "#fff" },
    rowBorder:    "#f1f5f9",
    input:        { bg: "#fff",       border: "#e2e8f0", color: "#0f172a" },
    sectionTitle: { color: "#0f172a" },
    toggleBtn:    { bg: "#fff",       border: "#e2e8f0", color: "#334155" },
    accent:       "#1d4ed8",
    accentText:   "#fff",
    saveBtn:      { bg: "#fff",       border: "#e2e8f0", color: "#334155" },
    modal:        { bg: "#fff",       border: "#e2e8f0" },
    modalDivider: "#e2e8f0",
  },
};

/* ============================================================
   CSS DYNAMIQUE
   ============================================================ */
function injectStyles(mode) {
  let el = document.getElementById("boisform-styles");
  if (!el) { el = document.createElement("style"); el.id = "boisform-styles"; document.head.appendChild(el); }

  const accent      = mode === "dark" ? "#e6ff3e"                : "#1d4ed8";
  const accentAlpha = mode === "dark" ? "rgba(230,255,62,0.12)"  : "rgba(29,78,216,0.08)";
  const accentFocus = mode === "dark" ? "rgba(230,255,62,0.5)"   : "rgba(29,78,216,0.5)";
  const accentShadow= mode === "dark" ? "rgba(230,255,62,0.1)"   : "rgba(29,78,216,0.1)";
  const btnHoverBg  = mode === "dark" ? "#fff"                   : "#2563eb";
  const btnHoverTx  = mode === "dark" ? "#000"                   : "#fff";
  const saveBtnHov  = mode === "dark" ? "rgba(255,255,255,0.12)" : "#f1f5f9";
  const rowHover    = mode === "dark" ? "rgba(255,255,255,0.02)" : "#f8fafc";

  el.innerHTML = `
    /* inputs & selects */
    .bf-input:focus, .bf-select:focus {
      outline: none;
      border-color: ${accentFocus} !important;
      box-shadow: 0 0 0 3px ${accentShadow} !important;
      background: ${accentAlpha} !important;
    }
    .bf-select option { background: ${mode === "dark" ? "#1e293b" : "#fff"}; color: ${mode === "dark" ? "#fff" : "#0f172a"}; }

    /* bouton accent (PRINT) */
    .bf-btn-accent:hover:not(:disabled) {
      background: ${btnHoverBg} !important;
      color: ${btnHoverTx} !important;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px ${accentShadow};
    }
    .bf-btn-accent:active { transform: scale(0.98) !important; }

    /* bouton save / update */
    .bf-btn-secondary:hover:not(:disabled) { background: ${saveBtnHov} !important; }
    .bf-btn-secondary:active { transform: scale(0.98) !important; }

    /* toggle */
    .bf-toggle:hover { opacity: 0.8; transform: scale(1.05); }

    /* ligne hover */
    .bf-row:hover .bf-cell-input { background: ${rowHover} !important; }

    /* modal boutons */
    .bf-modal-menu:hover  { background: ${btnHoverBg} !important; color: ${btnHoverTx} !important; transform: translateY(-1px); }
    .bf-modal-close:hover { opacity: 0.75; }

    /* responsive */
    @media (min-width: 640px)  {
      .bf-wrap  { padding: 0 24px 40px !important; }
      .bf-row   { grid-template-columns: 180px 1fr !important; }
      .bf-label { border-bottom: none !important; border-right: 1px solid var(--bf-row-border) !important; }
    }
    @media (min-width: 900px)  {
      .bf-wrap { padding: 0 40px 40px !important; }
      .bf-faa  { grid-template-columns: 2fr 1fr !important; }
    }
    @media (min-width: 1024px) { .bf-wrap { padding: 0 80px 40px !important; } }
    @media (min-width: 1280px) { .bf-wrap { padding: 0 120px 40px !important; } }
    @media (min-width: 1536px) { .bf-wrap { padding: 0 200px 40px !important; } }
    @media (max-width: 639px)  {
      .bf-btn { height: 48px !important; }
      .bf-input, .bf-select { height: 42px !important; font-size: 15px !important; }
    }
  `;
}

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function BoisForm() {
  const { t, ui } = useI18n();

  /* ── Thème ── */
  const [mode, setMode] = useState(() => localStorage.getItem("qsf-theme") || "dark");
  const T = THEMES[mode];
  useEffect(() => { injectStyles(mode); localStorage.setItem("qsf-theme", mode); }, [mode]);
  const toggleMode = () => setMode(m => m === "dark" ? "light" : "dark");

  const [options, setOptions] = useState({ essences: [], couleurs: [], coupes: [], fournisseurs: [] });
  const location   = useLocation();
  const navigate   = useNavigate();
  const [editing, setEditing]           = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [message, setMessage]           = useState("");
  const [messageType, setMessageType]   = useState(""); // "success" | "error" | "warn"
  const [submitting, setSubmitting]     = useState(false);
  const [printing, setPrinting]         = useState(false);
  const [updateOpen, setUpdateOpen]     = useState(false);
  const [certifPdfFile, setCertifPdfFile] = useState(null);
  const [certifPdfKey, setCertifPdfKey]   = useState("");
  const [certifPdfName, setCertifPdfName] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const initialFormData = {
    Essence: "", Couleur: "", Coupe: "", Fournisseur: "",
    Date: today, "Num de lot": "", QuantiteTotaleLot: "", NombreLot: "",
    Longueur: "", Largeur: "", Epaisseur: "", NombreDePlacage: "",
    PrixMetreCube: "", Humidité: "", Qualité: "", Certif: "",
    PN: "", CertifPdfKey: "", CertifPdfName: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  const showMsg = (text, type = "success") => { setMessage(text); setMessageType(type); };

  const onGoOptions = (type) => { setUpdateOpen(false); navigate(`/options/${type}`); };

  /* ── Helpers ── */
  const canon = (s) => String(s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().replace(/[^a-z0-9]/g,"");
  const nameFromCode = (list=[], code) => { const it = list.find(x => String(x.code).toUpperCase() === String(code||"").toUpperCase()); return it ? it.nom : ""; };
  function getCodeByName(list, name) {
    if (!name) return "";
    const raw = String(name).trim();
    const exact = list.find(x => String(x.nom||"").trim().toLowerCase() === raw.toLowerCase());
    if (exact) return exact.code;
    const ctarget = canon(raw);
    let hit = list.find(x => canon(x.nom) === ctarget);
    if (hit) return hit.code;
    hit = list.find(x => canon(x.nom).includes(ctarget) || ctarget.includes(canon(x.nom)));
    return hit ? hit.code : "";
  }
  const toYYMM = (val) => {
    if (!val) return "";
    const s = String(val).trim(); let m;
    if ((m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/))) return m[3].slice(-2)+m[2];
    if ((m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)))   return m[1].slice(-2)+m[2];
    if ((m = s.match(/^(\d{2})(\d{2})$/))) { const a=parseInt(m[1],10); return (a>=1&&a<=12)?m[2]+m[1]:s; }
    return s;
  };
  function isAndroid() { return /Android/i.test(navigator.userAgent||""); }

  /* ── Load options ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/options");
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!cancelled) {
          setOptions({ essences: data.essences||[], couleurs: data.couleurs||data.coloris||[], coupes: data.coupes||[], fournisseurs: data.fournisseurs||[] });
          setOptionsLoaded(true);
        }
      } catch { if (!cancelled) showMsg("Impossible de charger les listes.", "error"); }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── Pré-remplissage édition ── */
  useEffect(() => {
    if (!optionsLoaded) return;
    const fromState   = location.state?.item;
    const fromStorage = !fromState ? JSON.parse(localStorage.getItem("woodoo_edit_item")||"null") : null;
    const item = fromState || fromStorage;
    if (item) {
      item.Fournisseur = getCodeByName(options.fournisseurs, item.Fournisseur);
      setFormData(prev => ({ ...prev, ...item }));
      setCertifPdfKey(item.CertifPdfKey || "");
      setCertifPdfName(item.CertifPdfName || "");
      setCertifPdfFile(null);
      setEditing(true);
      localStorage.removeItem("woodoo_edit_item");
    }
  }, [optionsLoaded, location.state]);

  const handleChange = (e) => setFormData(s => ({ ...s, [e.target.name]: e.target.value }));

  const handleCertifPdfChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      showMsg("Seuls les fichiers PDF sont acceptés.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showMsg("PDF trop volumineux (10 Mo max).", "error");
      e.target.value = "";
      return;
    }
    setCertifPdfFile(file);
    setCertifPdfName(file.name);
  };

  const extractPnFromMessage = (message) => {
    const m = String(message || "").match(/PN:\s*(\S+)/i);
    return m ? m[1] : "";
  };

  const uploadCertifPdf = async (pn) => {
    if (!certifPdfFile || !pn) return null;
    const fd = new FormData();
    fd.append("pdf", certifPdfFile);
    fd.append("pn", pn);
    const r = await apiFetch("/api/logistique/bois/upload-certif", { method: "POST", body: fd });
    const res = await r.json();
    if (!r.ok || res.status !== "success") {
      throw new Error(res.message || "Erreur upload PDF certification");
    }
    return res;
  };

  const openCertifPdf = async () => {
    if (!certifPdfKey) return;
    try {
      const r = await apiFetch(`/api/logistique/bois/download-certif?key=${encodeURIComponent(certifPdfKey)}`);
      if (!r.ok) throw new Error("Impossible d'ouvrir le PDF");
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch (e) {
      showMsg(e.message || "Erreur ouverture PDF", "error");
    }
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    try {
      const payload = { ...formData };
      delete payload.CertifPdfKey;
      delete payload.CertifPdfName;
      if (!payload.PN) delete payload.PN;

      const url = editing ? "/api/update" : "/api/add";
      const r   = await apiFetch(url, { method: editing?"PUT":"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(payload) });
      const res = await r.json();
      if (res.status !== "success") {
        showMsg(res.message || "Erreur", "error");
        return;
      }

      let pn = res.pn || formData.PN || extractPnFromMessage(res.message);
      let pdfKey = certifPdfKey;
      let pdfName = certifPdfName;

      if (certifPdfFile && pn) {
        const up = await uploadCertifPdf(pn);
        pdfKey = up.certif_pdf_key;
        pdfName = up.certif_pdf_name || certifPdfFile.name;
        const r2 = await apiFetch("/api/update", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            PN: pn,
            CertifPdfKey: pdfKey,
            CertifPdfName: pdfName,
          }),
        });
        const res2 = await r2.json();
        if (res2.status !== "success") {
          showMsg(res2.message || "PDF uploadé mais enregistrement incomplet", "warn");
        }
      }

      setFormData(prev => ({ ...prev, PN: pn, CertifPdfKey: pdfKey, CertifPdfName: pdfName }));
      setCertifPdfKey(pdfKey);
      setCertifPdfName(pdfName);
      setCertifPdfFile(null);
      if (!editing) setEditing(true);

      showMsg(res.message || "Terminé", "success");
    } catch (err) { showMsg(err.message || "Erreur réseau", "error"); }
    finally       { setSubmitting(false); }
  };

  /* ── Print ── */
  const confirmPrint = async () => {
    const pn = [formData.Essence, formData.Couleur, formData.Coupe, toYYMM(formData.Date), formData.Fournisseur, formData["Num de lot"], formData.NombreLot]
      .filter(Boolean).join("_").replace(/[^\p{L}\p{N}_-]+/gu,"_").replace(/_+/g,"_").toUpperCase();
    if (!pn) { showMsg("Renseigne d'abord les champs clés (Essence, Couleur, Coupe, Date, Fournisseur, Numéro de lot).", "warn"); return; }
    const detail = [nameFromCode(options.essences,formData.Essence), nameFromCode(options.couleurs,formData.Couleur), formData.Epaisseur?`${formData.Epaisseur}mm`:"", nameFromCode(options.coupes,formData.Coupe)].filter(Boolean).join(" ");
    try {
      setPrinting(true);
      await clientPrintFromApi({ n:2, pn, detail, date:formData.Date, placage:formData.NombreDePlacage });
      showMsg("✅ Document prêt à l'impression (2 étiquette(s)).", "success");
    } catch (e) { showMsg(e.message||"Erreur réseau","error"); }
    finally     { setPrinting(false); }
  };

  async function clientPrintFromApi(bodyForApi) {
    const win = window.open("","_blank");
    if (win) { win.document.write(`<!doctype html><title>Préparation…</title><body style="margin:0;font:16px sans-serif">Préparation…</body>`); win.document.close(); }
    const res = await apiFetch("/api/labels/a4",{ method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({...bodyForApi, format:"PNG", download:false}) });
    if (!res.ok) { const err=await res.json().catch(()=>({})); throw new Error(err.message||`HTTP ${res.status}`); }
    const blob    = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const safePN  = String(bodyForApi.pn||"QR").replace(/[^\w\-]+/g,"_").slice(0,64);
    const fileName= `A4_${bodyForApi.n||1}x_${safePN}.png`;
    if (!win) { const a=document.createElement("a"); a.href=blobUrl; a.download=fileName; a.click(); URL.revokeObjectURL(blobUrl); return; }
    const ANDROID = isAndroid();
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${fileName}</title>
    <style>html,body{height:100%;margin:0}#bar{position:fixed;inset:auto 0 0 0;background:#111;color:#fff;padding:8px;display:flex;gap:8px;align-items:center;z-index:10}#bar button,#bar a{background:#E6FF3E;color:#000;border:0;padding:8px 12px;font-weight:800;cursor:pointer;text-decoration:none}.viewer{position:fixed;left:0;right:0;top:0;bottom:44px;border:0;width:100%;height:auto;min-height:calc(100% - 44px);object-fit:contain;background:#fff}</style></head><body>
    <img class="viewer" src="${blobUrl}" alt="Étiquettes" />
    <div id="bar">${ANDROID?"":`<button id='btnPrint'>Imprimer</button>`}<a id="btnDownload" href="${blobUrl}" download="${fileName}">Télécharger</a><button id="btnClose">Fermer</button></div>
    <script>${ANDROID?"":"btnPrint.onclick=()=>{try{window.focus();window.print();}catch(e){}};"}btnClose.onclick=()=>{try{URL.revokeObjectURL("${blobUrl}");}catch(e){}setTimeout(()=>window.close(),100);};window.onafterprint=()=>{try{URL.revokeObjectURL("${blobUrl}");}catch(e){}setTimeout(()=>window.close(),200);};${ANDROID?"":"setTimeout(()=>{try{window.focus();window.print();}catch(e){}},400);"}<\/script></body></html>`;
    win.document.open(); win.document.write(html); win.document.close();
  }

  /* ── Styles thémés ── */
  const s = {
    page:       { minHeight:"100vh", background:T.page.bg, color:T.page.color, fontFamily:'system-ui,-apple-system,"Segoe UI",Roboto,sans-serif', transition:"background 0.3s,color 0.3s" },
    wrap:       { padding:"0 16px 36px" },
    topBar:     { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 0 0" },
    sectionLeft:{ display:"flex", alignItems:"center", gap:12 },
    badge:      { background:T.accent, color:T.accentText, fontWeight:900, fontSize:11, letterSpacing:"0.08em", textTransform:"uppercase", padding:"4px 12px", borderRadius:4 },
    sectionTitle:{ fontWeight:900, fontSize:18, letterSpacing:"0.04em", textTransform:"uppercase", color:T.sectionTitle.color },
    toggleBtn:  { display:"flex", alignItems:"center", gap:8, background:T.toggleBtn.bg, border:`1px solid ${T.toggleBtn.border}`, color:T.toggleBtn.color, borderRadius:10, padding:"8px 14px", cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit", transition:"all 0.2s" },

    panel:      { background:"transparent", padding:"16px 0", marginTop:8 },

    card:       { background:T.card.bg, border:`1px solid ${T.card.border}`, borderRadius:16, overflow:"hidden", boxShadow:"0 8px 32px rgba(0,0,0,0.15)", transition:"all 0.3s" },
    cardHeader: { padding:"14px 20px", background:T.cardHeader.bg, borderBottom:`1px solid ${T.cardHeader.border}`, fontWeight:800, fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", color:T.cardHeader.color },

    faa:        { display:"grid", gridTemplateColumns:"1fr", gap:20, padding:16 },
    form:       { background:T.card.bg, border:`1px solid ${T.card.border}`, borderRadius:12, overflow:"hidden" },

    row:        { display:"grid", gridTemplateColumns:"1fr", borderBottom:`1px solid ${T.rowBorder}`, transition:"background 0.15s" },
    cellLabel:  { background:T.cellLabel.bg, color:T.cellLabel.color, fontWeight:700, padding:"11px 14px", fontSize:11, letterSpacing:"0.07em", textTransform:"uppercase", borderBottom:`1px solid ${T.cellLabel.border}` },
    cellInput:  { background:T.cellInput.bg, padding:"8px 12px", transition:"background 0.15s" },

    input:      { width:"100%", height:36, borderRadius:8, border:`1px solid ${T.input.border}`, padding:"0 10px", fontSize:14, background:T.input.bg, color:T.input.color, fontFamily:"inherit", transition:"all 0.2s" },

    actions:    { display:"flex", flexDirection:"column", gap:10, alignItems:"stretch" },

    btnAccent:  { height:44, border:"none", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.06em", cursor:"pointer", borderRadius:10, fontSize:13, background:T.accent, color:T.accentText, fontFamily:"inherit", transition:"all 0.2s" },
    btnSave:    { height:44, border:`1px solid ${T.saveBtn.border}`, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.06em", cursor:"pointer", borderRadius:10, fontSize:13, background:T.saveBtn.bg, color:T.saveBtn.color, fontFamily:"inherit", transition:"all 0.2s" },

    msgBox:     (type) => ({
      marginTop:10, padding:"10px 14px", borderRadius:8, fontWeight:700, fontSize:13, lineHeight:1.4,
      ...(type==="success" ? { background:"rgba(74,222,128,0.1)",  border:"1px solid rgba(74,222,128,0.25)", color:"#4ade80" } :
          type==="error"   ? { background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.25)",color:"#f87171" } :
                             { background:"rgba(251,191,36,0.1)",  border:"1px solid rgba(251,191,36,0.25)", color:"#fbbf24" }),
    }),

    /* modal */
    backdrop:   { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding:20, backdropFilter:"blur(4px)" },
    modal:      { width:"100%", maxWidth:360, background:T.modal.bg, border:`1px solid ${T.modal.border}`, borderRadius:16, boxShadow:"0 24px 60px rgba(0,0,0,0.5)", overflow:"hidden" },
    modalTitle: { fontWeight:800, padding:"16px 20px", borderBottom:`1px solid ${T.modalDivider}`, fontSize:14, color:T.sectionTitle.color },
    modalBody:  { padding:16, display:"flex", flexDirection:"column", gap:8 },
    modalFoot:  { display:"flex", justifyContent:"flex-end", padding:"12px 16px", borderTop:`1px solid ${T.modalDivider}` },
    modalMenu:  { width:"100%", background:T.accent, color:T.accentText, border:"none", padding:"12px 16px", fontWeight:800, cursor:"pointer", borderRadius:8, fontSize:13, fontFamily:"inherit", transition:"all 0.2s", textAlign:"left" },
    modalClose: { background:"transparent", color:T.sectionTitle.color, border:`1px solid ${T.modal.border}`, padding:"8px 18px", cursor:"pointer", borderRadius:8, fontWeight:700, fontSize:13, fontFamily:"inherit", transition:"all 0.2s" },
  };

  const FIELDS_SELECT = [
    { label:ui("ESSENCE"),      name:"Essence",    opts: options.essences    },
    { label:ui("COLORIS"),      name:"Couleur",    opts: options.couleurs    },
    { label:ui("COUPE"),        name:"Coupe",      opts: options.coupes      },
    { label:ui("FOURNISSEUR"),  name:"Fournisseur",opts: options.fournisseurs },
  ];
  const FIELDS_INPUT = [
    { label:ui("DATE"),                      name:"Date",             type:"date"   },
    { label:ui("NUMÉRO DU LOT FOURNISSEUR"), name:"Num de lot",       type:"text"   },
    { label:ui("QUANTITÉ TOTALE DE LOT"),    name:"QuantiteTotaleLot",type:"number" },
    { label:ui("NOMBRE DE LOT"),             name:"NombreLot",        type:"number" },
    { label:ui("LONGUEUR (mm)"),             name:"Longueur",         type:"number" },
    { label:ui("LARGEUR (mm)"),              name:"Largeur",          type:"number" },
    { label:ui("ÉPAISSEUR (mm)"),            name:"Epaisseur",        type:"number" },
  ];

  return (
    <div style={s.page}>
      <MenuBar active="logistique" />

      <div style={s.wrap} className="bf-wrap">

        {/* ── Top bar ── */}
        <div style={s.topBar}>
          <div style={s.sectionLeft}>
            <span style={s.badge}>{ui("Logistique")}</span>
            <span style={s.sectionTitle}>{ui("Suivi de livraison")}</span>
          </div>
          <button style={s.toggleBtn} className="bf-toggle" onClick={toggleMode}>
            <span style={{ fontSize:16 }}>{mode==="dark"?"☀️":"🌙"}</span>
            <span>{mode==="dark"?ui("Clair"):ui("Sombre")}</span>
          </button>
        </div>

        {/* ── Card ── */}
        <div style={{ ...s.card, marginTop:20 }}>
          <div style={s.cardHeader}>{ui("Paramètres de réception bois")}</div>

          <div style={s.faa} className="bf-faa">

            {/* ── Formulaire ── */}
            <form onSubmit={handleSubmit} style={s.form}>

              {/* Selects */}
              {FIELDS_SELECT.map(({ label, name, opts }) => (
                <BfRow key={name} label={label} s={s} T={T}>
                  <select name={name} value={formData[name]} onChange={handleChange}
                    style={s.input} className="bf-select bf-input">
                    <option value="">{ui("— Sélectionner —")}</option>
                    {opts.map(o => <option key={o.code} value={o.code}>{o.nom}</option>)}
                  </select>
                </BfRow>
              ))}

              {/* Inputs simples */}
              {FIELDS_INPUT.map(({ label, name, type }) => (
                <BfRow key={name} label={label} s={s} T={T}>
                  <input type={type} name={name} value={formData[name]} onChange={handleChange}
                    style={s.input} className="bf-input" />
                </BfRow>
              ))}

              {/* Qualité */}
              <BfRow label={ui("QUALITÉ")} s={s} T={T}>
                <select name="Qualité" value={formData.Qualité} onChange={handleChange}
                  style={s.input} className="bf-select bf-input">
                  <option value="">{ui("— Sélectionner —")}</option>
                  {["Faces","Ames","interieur"].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </BfRow>

              {/* Certification */}
              <BfRow label="CERTIFICATION" s={s} T={T}>
                <select name="Certif" value={formData.Certif} onChange={handleChange}
                  style={s.input} className="bf-select bf-input">
                  <option value="">{ui("— Sélectionner —")}</option>
                  {["FSC","PEFC","Bois de France"].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </BfRow>

              {/* PDF certification */}
              <BfRow label="CERTIFICAT PDF" s={s} T={T}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleCertifPdfChange}
                    style={{ ...s.input, padding: "6px 10px", height: "auto" }}
                    className="bf-input"
                  />
                  {(certifPdfFile || certifPdfKey) && (
                    <div style={{ fontSize: 12, color: "#94a3b8", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                      <span>📄 {certifPdfFile?.name || certifPdfName || "certificat.pdf"}</span>
                      {certifPdfKey && !certifPdfFile && (
                        <button type="button" onClick={openCertifPdf}
                          style={{ background: "transparent", border: `1px solid ${T.accent}`, color: T.accent, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                          Voir le PDF
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </BfRow>

              {/* Humidité */}
              <BfRow label="HUMIDITÉ (%RH)" s={s} T={T}>
                <input type="text" name="Humidité" value={formData.Humidité} onChange={handleChange}
                  style={s.input} className="bf-input" />
              </BfRow>

              {/* Nombre de placage */}
              <BfRow label="NOMBRE DE PLACAGE TOTAL" s={s} T={T}>
                <input type="number" name="NombreDePlacage" value={formData.NombreDePlacage} onChange={handleChange}
                  style={s.input} className="bf-input" />
              </BfRow>

              {/* Prix */}
              <BfRow label="PRIX AU MÈTRE CUBE (€)" s={s} T={T}>
                <input type="number" step="0.01" name="PrixMetreCube" value={formData.PrixMetreCube} onChange={handleChange}
                  style={s.input} className="bf-input" />
              </BfRow>

              <div style={{ display:"none" }}><button type="submit">Save</button></div>
            </form>

            {/* ── Actions ── */}
            <div style={s.actions} className="bf-actions">

              <button onClick={handleSubmit} disabled={submitting}
                style={s.btnSave} className="bf-btn-secondary bf-btn">
                {submitting ? (editing?"Mise à jour…":"Enregistrement…") : (editing?"✏️ UPDATE":"💾 SAVE")}
              </button>

              <button onClick={confirmPrint} disabled={printing}
                style={s.btnAccent} className="bf-btn-accent bf-btn">
                {printing ? "Impression…" : "🖨️ PRINT"}
              </button>

              <button onClick={() => setUpdateOpen(true)}
                style={{ ...s.btnSave, marginTop:4 }} className="bf-btn-secondary bf-btn">
                ⚙️ UPDATE TABLE
              </button>

              {message && (
                <div style={s.msgBox(messageType)}>{message}</div>
              )}

              {/* ── Séparateur infos ── */}
              {editing && (
                <div style={{ marginTop:8, padding:"10px 14px", borderRadius:8, background:"rgba(230,255,62,0.06)", border:`1px solid rgba(230,255,62,0.15)`, fontSize:12, color:"#e6ff3e", fontWeight:700 }}>
                  ✏️ Mode édition actif
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modale Update Table ── */}
      {updateOpen && (
        <div style={s.backdrop}>
          <div style={s.modal}>
            <div style={s.modalTitle}>⚙️ Mettre à jour une table d'options</div>
            <div style={s.modalBody}>
              {["couleurs","coupes","essences","fournisseurs"].map(type => (
                <button key={type} style={s.modalMenu} className="bf-modal-menu"
                  onClick={() => onGoOptions(type)}>
                  {type.charAt(0).toUpperCase()+type.slice(1)}
                </button>
              ))}
            </div>
            <div style={s.modalFoot}>
              <button onClick={() => setUpdateOpen(false)} style={s.modalClose} className="bf-modal-close">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   SOUS-COMPOSANT ROW
   ============================================================ */
function BfRow({ label, children, s, T }) {
  return (
    <div style={s.row} className="bf-row">
      <div style={s.cellLabel} className="bf-label" data-bf-row-border={T.rowBorder}>
        {label}
      </div>
      <div style={s.cellInput} className="bf-cell-input">
        {children}
      </div>
    </div>
  );
}
